import React from "react";
import {
  Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Typography,
} from "@mui/material";
import { formatTime, parseDateInfo } from "./sessionsModel";

const REASON = {
  early: "This session hasn't reached its scheduled day yet.",
  closed: "This session's scheduled day has already passed.",
};

const scheduleOf = (session) => {
  const info = parseDateInfo(session.sessionDate || session.date);
  const day = info ? `${info.weekday}, ${info.month} ${info.day}` : "No date set";
  const times = session.startTime && session.endTime ? ` · ${formatTime(session.startTime)} – ${formatTime(session.endTime)}` : "";
  return `${day}${times}`;
};

/**
 * Asked before the check-in code is shown for a session whose scheduled day is not today. The instructor must tick the
 * box to confirm they know it is not the scheduled time and accept the risk; the server records that acceptance.
 */
export default function OffHoursConfirmDialog({ open, session, state, onCancel, onConfirm }) {
  const [accepted, setAccepted] = React.useState(false);

  React.useEffect(() => {
    if (open) setAccepted(false);
  }, [open, session?.id]);

  if (!open || !session) return null;

  return (
    <Dialog open onClose={onCancel} maxWidth="xs" fullWidth aria-labelledby="off-hours-title">
      <DialogTitle id="off-hours-title" sx={{ fontWeight: 700 }}>Show the code outside the scheduled time?</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          {REASON[state] ?? "It isn't the scheduled time for this session."}
        </Alert>
        <Box sx={{ mb: 2 }} data-testid="off-hours-schedule">
          <Typography sx={{ fontWeight: 600 }}>{session.name || session.title || "This session"}</Typography>
          <Typography sx={{ fontSize: 14, color: "text.secondary" }}>Scheduled: {scheduleOf(session)}</Typography>
          <Typography sx={{ fontSize: 14, color: "text.secondary" }}>Now: {new Date().toLocaleString()}</Typography>
        </Box>
        <Typography sx={{ fontSize: 14, mb: 1.5 }}>
          Students can check in from the code until it lapses, 12 hours from now, wherever they are. Your name, this
          session and the time are saved to the audit log.
        </Typography>
        <FormControlLabel
          control={<Checkbox checked={accepted} onChange={(e) => setAccepted(e.target.checked)} />}
          label="I know it isn't the scheduled time for this session and I accept the risk of showing the check-in code off hours."
          sx={{ alignItems: "flex-start", "& .MuiCheckbox-root": { pt: 0.5 } }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onCancel} sx={{ textTransform: "none" }}>Cancel</Button>
        <Button variant="contained" disabled={!accepted} onClick={onConfirm} sx={{ textTransform: "none" }}>
          Show QR code anyway
        </Button>
      </DialogActions>
    </Dialog>
  );
}
